import {useMediaQueries} from 'presentation/pages';
import {UseBeBornWellDrawerProps} from './types';
import {useTheme} from '@mui/material';
import {useState} from 'react';
import {useHostName, usePersonalData} from 'presentation/hooks';
import {useAcceptWellBornProgramMutation} from 'presentation/modules/booking/shared/mutations/useAcceptWellBornProgramMutation';
import {useDeclineWellBornProgramMutation} from 'presentation/modules/booking/shared/mutations/useDeclineWellBornProgramMutation';
import {useWellBornEligibility} from 'presentation/modules/booking/shared/hooks/useWellBornEligibility';
import {WellBornEndpoints} from 'presentation/modules/booking/shared/constants/beBornWell';
import {translations} from 'presentation/translations-new';

const {beBornWellStep, carryPregnancyWellStep} =
	translations['pt-br'].privateFlow.bookingFlow.beBornWellFlow.beBornWellDrawer;

export function useBeBornWellDrawer({
	setOpen,
	handleNotPregnant,
	handleContinueToBooking,
	onShowBeBornWellCard,
	onShowBeBornWellNotRegister,
}: UseBeBornWellDrawerProps) {
	const {isMobile} = useMediaQueries();
	const theme = useTheme();
	const [currentStep, setCurrentStep] = useState(1);
	const {currentPhone} = usePersonalData();
	const {isGndiSaoPaulo, isGndiMinas} = useHostName();
	const {eligibilityData} = useWellBornEligibility();

	const acceptWellBornProgramMutation = useAcceptWellBornProgramMutation();
	const declineWellBornProgramMutation = useDeclineWellBornProgramMutation();

	const isStepOne = currentStep === 1;
	const isError = acceptWellBornProgramMutation.isError || declineWellBornProgramMutation.isError;
	const declineIsLoading = declineWellBornProgramMutation.isPending;
	const acceptIsLoading = acceptWellBornProgramMutation.isPending;
	const disabledButtons = (declineIsLoading || acceptIsLoading) && !isStepOne;

	const handleClose = () => {
		setOpen(false);
	};

	const handlePregnantClick = () => {
		setCurrentStep(prev => prev + 1);
	};

	const handleNotPregnantClick = () => {
		handleNotPregnant();
		setOpen(false);
	};

	const handleRegisterClick = async () => {
		await acceptWellBornProgramMutation.mutateAsync({phoneNumber: currentPhone});
		if (onShowBeBornWellCard) {
			onShowBeBornWellCard();
		}
		setOpen(false);
	};

	const handleNoInterestClick = async () => {
		await declineWellBornProgramMutation.mutateAsync();
		if (isGndiSaoPaulo || isGndiMinas) {
			handleContinueToBooking();
			setOpen(false);
		} else {
			if (onShowBeBornWellNotRegister) {
				onShowBeBornWellNotRegister();
			}
			setOpen(false);
		}
	};

	const handleRetry = async () => {
		if (declineWellBornProgramMutation.isError) {
			await declineWellBornProgramMutation.mutateAsync();
		} else if (acceptWellBornProgramMutation.isError) {
			await acceptWellBornProgramMutation.mutateAsync({phoneNumber: currentPhone});
		}
	};

	const getStep2Content = () => {
		if (eligibilityData?.cidadePrograma === WellBornEndpoints.WELL_BORN) {
			return beBornWellStep;
		}
		if (eligibilityData?.cidadePrograma === WellBornEndpoints.CARRY_PREGNANCY_WELL) {
			return carryPregnancyWellStep;
		}

		return beBornWellStep;
	};

	const step2Content = getStep2Content();

	return {
		step2Content,
		isError,
		isMobile,
		currentStep,
		isStepOne,
		theme,
		handleClose,
		handlePregnantClick,
		handleNotPregnantClick,
		handleRegisterClick,
		handleNoInterestClick,
		handleRetry,
		declineIsLoading,
		acceptIsLoading,
		disabledButtons,
	};
}
